import React, { useState, useEffect } from 'react';
import { supabase_get_client_settings, supabase_update_client_settings, extractErrorMessage } from '../services/supabaseService';
import { Loader2, AlertCircle, CheckCircle2, ListPlus, X, GripVertical, Palette } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

interface TalentPoolStatusesSettingsTabProps {
    clientId: string;
}

const COLORS = [
    { label: 'Rood', value: 'bg-red-50 text-red-600 border-red-100' },
    { label: 'Geel', value: 'bg-yellow-50 text-yellow-700 border-yellow-100' },
    { label: 'Groen', value: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
    { label: 'Blauw', value: 'bg-blue-50 text-blue-700 border-blue-100' },
    { label: 'Paars', value: 'bg-purple-50 text-purple-700 border-purple-100' },
    { label: 'Oranje', value: 'bg-amber-50 text-amber-700 border-amber-100' },
    { label: 'Indigo', value: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
    { label: 'Cyaan', value: 'bg-cyan-50 text-cyan-700 border-cyan-100' },
    { label: 'Grijs', value: 'bg-gray-50 text-gray-600 border-gray-100' }
];

export const TalentPoolStatusesSettingsTab: React.FC<TalentPoolStatusesSettingsTabProps> = ({ clientId }) => {
    const [settings, setSettings] = useState<any>(null);
    const [statuses, setStatuses] = useState<{id: string, label: string, color: string}[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    
    const [newLabel, setNewLabel] = useState('');
    const [newColor, setNewColor] = useState(COLORS[8].value);

    useEffect(() => {
        if (clientId) fetchSettings();
    }, [clientId]);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const data = await supabase_get_client_settings(clientId);
            setSettings(data || {});
            
            if (data?.talentpool_statuses && Array.isArray(data.talentpool_statuses)) {
                setStatuses(data.talentpool_statuses);
            } else {
                setStatuses([
                    { id: 'lead', label: 'Lead', color: 'bg-red-50 text-red-600 border-red-100' },
                    { id: 'shortlist', label: 'Shortlist', color: 'bg-yellow-50 text-yellow-700 border-yellow-100' },
                    { id: 'active', label: 'Actief', color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
                    { id: 'beschikbaar', label: 'Beschikbaar (Open to work)', color: 'bg-sky-50 text-sky-700 border-sky-100' },
                    { id: 'onder contract', label: 'Onder contract', color: 'bg-amber-50 text-amber-700 border-amber-100' },
                    { id: 'placed', label: 'Geplaatst', color: 'bg-purple-50 text-purple-700 border-purple-100' },
                    { id: 'wp-interim', label: 'wp-interim', color: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
                    { id: 'werkenbijvideoworks', label: 'werkenbijvideoworks.nl', color: 'bg-blue-50 text-blue-700 border-blue-100' },
                    { id: 'website sollicitatie', label: 'Website Sollicitatie', color: 'bg-cyan-50 text-cyan-700 border-cyan-100' },
                    { id: 'archived', label: 'Gearchiveerd', color: 'bg-gray-50 text-gray-600 border-gray-100' },
                    { id: 'blacklisted', label: 'Blacklist', color: 'bg-red-50 text-red-700 border-red-100' }
                ]);
            }
        } catch (e: any) {
            console.error("Error fetching client settings:", e);
            setError(extractErrorMessage(e));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (updatedStatuses: any[] = statuses) => {
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const newSettings = { ...settings, talentpool_statuses: updatedStatuses };
            await supabase_update_client_settings(clientId, newSettings);
            setSettings(newSettings);
            setSuccess("Statussen succesvol opgeslagen!");
            setTimeout(() => setSuccess(null), 3000);
        } catch (e: any) {
            setError(extractErrorMessage(e));
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddStatus = () => {
        if (!newLabel.trim()) return;
        const id = newLabel.trim().toLowerCase();
        if (statuses.some(s => s.id === id || s.label.toLowerCase() === newLabel.trim().toLowerCase())) {
            setError("Deze status bestaat al.");
            return;
        }
        
        const updated = [...statuses, { id, label: newLabel.trim(), color: newColor }];
        setStatuses(updated);
        setNewLabel('');
        setNewColor(COLORS[8].value);
        handleSave(updated);
    };

    const handleRemoveStatus = (indexToRemove: number) => {
        const updated = statuses.filter((_, idx) => idx !== indexToRemove);
        setStatuses(updated);
        handleSave(updated);
    };

    const onDragEnd = (result: any) => {
        if (!result.destination) return;
        const items = Array.from(statuses);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setStatuses(items);
        handleSave(items);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center p-10">
                <Loader2 className="animate-spin text-slate-400" size={32} />
            </div>
        );
    }

    return (
        <div className="max-w-3xl space-y-8 animate-fadeIn">
            <div>
                <h2 className="text-xl font-black text-slate-900 tracking-tight">Talentpool Statussen</h2>
                <p className="text-sm text-slate-500 font-medium mt-1">
                    Beheer de lijst met statussen (inclusief kleur) voor kandidaten in de talentpool. Deze worden gebruikt bij het toevoegen, bewerken en filteren van kandidaten.
                </p>
                {error && <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-xl flex items-center gap-3 mt-4"><AlertCircle size={20} /><span className="text-xs font-bold">{error}</span></div>}
                {success && <div className="bg-emerald-50 border border-emerald-100 text-emerald-600 p-4 rounded-xl flex items-center gap-3 mt-4"><CheckCircle2 size={20} /><span className="text-xs font-bold">{success}</span></div>}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 overflow-hidden">
                <div className="flex gap-2 mb-6 flex-wrap md:flex-nowrap">
                    <input 
                        type="text" 
                        value={newLabel} 
                        onChange={(e) => setNewLabel(e.target.value)} 
                        onKeyDown={(e) => e.key === 'Enter' && handleAddStatus()}
                        placeholder="Nieuwe status (bijv. Aangenomen)" 
                        className="flex-1 min-w-[200px] px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-slate-400"
                    />
                    <select
                        value={newColor}
                        onChange={(e) => setNewColor(e.target.value)}
                        className="w-full md:w-auto px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-slate-400"
                    >
                        {COLORS.map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                    </select>
                    <button 
                        onClick={handleAddStatus} 
                        disabled={!newLabel.trim() || isSaving}
                        className="px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <ListPlus size={16} />} Toevoegen
                    </button>
                </div>

                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="statuses-list">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                                {statuses.map((status, index) => (
                                    <Draggable key={status.id} draggableId={status.id} index={index}>
                                        {(provided) => (
                                            <div 
                                                ref={provided.innerRef} 
                                                {...provided.draggableProps} 
                                                className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-xl shadow-sm hover:border-slate-200 hover:shadow-md transition-all group"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div {...provided.dragHandleProps} className="text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing">
                                                        <GripVertical size={18} />
                                                    </div>
                                                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${status.color}`}>
                                                        {status.label}
                                                    </span>
                                                </div>
                                                <button 
                                                    onClick={() => handleRemoveStatus(index)}
                                                    className="w-8 h-8 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                {statuses.length === 0 && (
                    <div className="text-center py-10 text-slate-400 font-medium text-sm">
                        Geen statussen gevonden. Voeg er een toe om te beginnen.
                    </div>
                )}
            </div>
        </div>
    );
};
